import { apiService } from './api.service';
import { PaginatedResponse } from 'types/index';
import { User } from 'types/user';
import type { Company } from 'types/company';
import api_no_token_Service from './api_no_token.service';
import { normalizePaginatedResponse } from 'utils/pagination';
import { unwrapEntity, unwrapOrThrow } from 'utils/apiResponse';

export type UserListOptions = {
  search?: string;
  emailSearch?: string;
};

class UsersService {
  async list(
    page: number = 1,
    limit: number = 100,
    options?: UserListOptions
  ): Promise<PaginatedResponse<User>> {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(limit),
    });
    if (options?.search?.trim()) {
      params.set('search', options.search.trim());
    }
    if (options?.emailSearch?.trim()) {
      params.set('emailSearch', options.emailSearch.trim());
    }

    const response = await apiService.get<PaginatedResponse<User>>(
      `/user/GetPageList?${params.toString()}`
    );

    return normalizePaginatedResponse<User>(response, {
      page,
      limit,
      fallbackMessage: 'Failed to load users',
    });
  }

  async getById(id: string): Promise<User> {
    const response = await apiService.get<any>(`/user/${id}`);
    const payload: any = (response as any)?.data ?? response;
    const userData = payload?.data && typeof payload.data === 'object' ? payload.data : typeof payload === 'object' ? payload : null;
    if (userData) return userData as User;
    return response as unknown as User;
  }

  async getRegisterData(id: string): Promise<{ user: User; company: Company | null }> {
    const response = await api_no_token_Service.get<any>(`/user/GetRegisterData/${id}`);
    const payload: any = (response as any)?.data ?? response;
    
    if (payload?.success && payload?.user) {
      return {
        user: payload.user as User,
        company: (payload.company as Company | null | undefined) || null
      };
    }
    
    throw new Error(payload?.error || 'Failed to load register data');
  }

  async create(payload: Partial<User>): Promise<User> {
    const response = await apiService.post<User>(`/user`, payload);
    return unwrapEntity<User>(response);
  }

  async createUser(payload: Partial<User>): Promise<User> {
    const response = await api_no_token_Service.post<User>(`/user/CreateRegistrationAccount`, payload);
    return unwrapEntity<User>(response);
  }

  async update(id: string, payload: Partial<User>): Promise<User> {
    const response = await apiService.put<User>(`/user/${id}`, payload);
    return unwrapEntity<User>(response);
  }

  async remove(id: string): Promise<void> {
    const response = await apiService.delete(`/user/${id}`);
    unwrapOrThrow(response, 'Delete failed');
  }
}

export const usersService = new UsersService();
export default usersService;
